import React, { useEffect, useRef } from 'react';
import './Modal.css';

/**
 * The outermost box in the dialog with content below its fold: usually the body,
 * or the dialog itself when it scrolls as one piece. Some modals wrap the body in a
 * form, so the search goes two levels down.
 */
function findScrollArea(dialog: HTMLElement): HTMLElement | null {
  let level: Element[] = [dialog];
  for (let depth = 0; depth <= 2 && level.length > 0; depth++) {
    for (const el of level) {
      if (!(el instanceof HTMLElement) || el.scrollHeight <= el.clientHeight + 1) continue;
      const { overflowY } = window.getComputedStyle(el);
      if (overflowY === 'auto' || overflowY === 'scroll') return el;
    }
    level = level.flatMap((el) => Array.from(el.children));
  }
  return null;
}

/** Wheel deltas arrive in pixels, lines or pages depending on the device and browser. */
function wheelPixels(event: React.WheelEvent, area: HTMLElement): number {
  if (event.deltaMode === 1) return event.deltaY * 40;
  if (event.deltaMode === 2) return event.deltaY * area.clientHeight;
  return event.deltaY;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** Increases max-width to 700px */
  wide?: boolean;
  /** Wider than `wide` (spreadsheet-style dialogs). */
  extraWide?: boolean;
  /** Decreases max-width to 460px */
  narrow?: boolean;
  /** Stack above another open modal (e.g. alert on top of add-inventory). */
  elevated?: boolean;
  /** Expand the modal to fill (nearly) the entire viewport. */
  fullScreen?: boolean;
  /** Appended to modal-content (e.g. print-scoping class). */
  contentClassName?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  wide = false,
  extraWide = false,
  narrow = false,
  elevated = false,
  fullScreen = false,
  contentClassName,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // The page behind is locked while a modal is open, so a wheel over the dimmed
  // backdrop would otherwise scroll nothing. The buttons that open a modal often sit
  // outside its footprint, which leaves the pointer there when it appears.
  const scrollFromBackdrop = (event: React.WheelEvent<HTMLDivElement>) => {
    if (event.ctrlKey || event.target !== event.currentTarget || !contentRef.current) return;
    const area = findScrollArea(contentRef.current);
    if (area) area.scrollTop += wheelPixels(event, area);
  };

  if (!isOpen) return null;

  const sizeClass = fullScreen
    ? ' modal-content--fullscreen'
    : extraWide
    ? ' modal-content--extra-wide'
    : wide
    ? ' modal-content--wide'
    : narrow
    ? ' modal-content--narrow'
    : '';

  return (
    <div
      className={`modal-overlay${elevated ? ' modal-overlay--elevated' : ''}${fullScreen ? ' modal-overlay--fullscreen' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-heading"
      onWheel={scrollFromBackdrop}
    >
      <div
        ref={contentRef}
        className={`modal-content${sizeClass}${contentClassName ? ` ${contentClassName}` : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 className="modal-title" id="modal-heading">{title}</h3>
          <button
            type="button"
            className="modal-close-button"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export default Modal;
